import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { callCreateCustomRulesGet, callCustomRulesGetSuccessful, callCustomRulesGetFailed, callCreateCustomRulesPost, callCustomRulesPostFailed, callCustomRulesPostSuccessful, callCustomRulesDeleteByRuleId, callCustomRulesDeleteFailed, callCustomRulesDeleteSuccessful } from '../../initiate-and-release/actions/init-release.actions';
import { Store, select } from '@ngrx/store';
import { withLatestFrom } from 'rxjs/operators';
import { selectCreateCustomRuleSection } from '../../initiate-and-release/reducers';
import { CreateCustomRule, CustomRulePostRequest, CustomRuleAttributes } from '../../initiate-and-release/Models/CreateCustomRule/CreateCustomRule';
import { CustomRuleInitiateAndReleaseService } from '../../create-request/services/custom-rule-initiate-and-release.service';

@Injectable()
export class CustomRuleInitiateAndReleaseEffects {
    constructor(
        private actions$: Actions,
        private customRuleService: CustomRuleInitiateAndReleaseService,
        private store: Store,
    ) {}

    createRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(callCreateCustomRulesPost),
            withLatestFrom(this.store.pipe(select(selectCreateCustomRuleSection))),
            switchMap(([action, customRuleFormValues]) => {
                let request = this.createCustomRulePostRequest(customRuleFormValues);
                return this.customRuleService.callCreateCustomRulesPost(request).pipe(
                    map(response => callCustomRulesPostSuccessful()),
                    catchError(error => of(callCustomRulesPostFailed()))
                );
            })
        )
    );

    deleteCustomRuleRequest$ = createEffect(() => 
        this.actions$.pipe(
            ofType(callCustomRulesDeleteByRuleId),
            switchMap((action) => {
                return this.customRuleService.callCustomRulesDelete(action.ruleId).pipe(
                  map(response => callCustomRulesDeleteSuccessful()),
                  catchError(error => of(callCustomRulesDeleteFailed()))
                );
            })
        )
    );

    createCustomRulePostRequest(createCustomRule: CreateCustomRule): CustomRulePostRequest {
        const isStandingAuth = createCustomRule.ruleType === 'Standing-Authorization';

        const attributes: any = {
            contextName: createCustomRule.ruleName,
            party: this.createPartyList(createCustomRule.createRuleParty),
            ruleType : this.mapRuleType(createCustomRule.ruleType)
        };  

        if   (!isStandingAuth) {
            attributes.threshold = Number(createCustomRule.ruleAmount.replaceAll(',', '').replace('$', ''));
            attributes.authorization = this.createAuthorizationList(createCustomRule.createRuleAuthorization);
            attributes.transactionTypes = this.createTransactionTypesList(createCustomRule.createRuleTransactionType);
        }
        return {
            data: {
              type: 'custom-rule-create-rule-request',
              attributes
            }
        };
    }

    mapRuleType(ruleType: string): string  {
        const mapping: { [key: string]: string } = {
            'Transaction': 'new-transactions',
            'Standing-Authorization': 'new-standing-authorizations'
        };
        return mapping[ruleType];
    }

    createTransactionTypesList(transactionTypes: any): string[] {
        const mapping = {
            createRuleCheck: 'check',
            createRuleIt: 'internal-transfer',
            createRuleWire: 'wire',
            createRuleAch: 'ach'
        };

        return transactionTypes.createRuleAllTransactionType
            ? Object.values(mapping)
            : Object.entries(mapping)
                .filter(([key]) => transactionTypes[key])
                .map(([, value]) => value);
    }

    createPartyList(parties: any): string[] {
        const mapping = {
            createRuleFirstParty: '1st-party',
            createRuleThirdParty: '3rd-party'
        };

        return parties.createRuleAllParty
            ? Object.values(mapping)
            : Object.entries(mapping)
                .filter(([key]) => parties[key])
                .map(([, value]) => value);
    }

    createAuthorizationList(authorizations: any): string[] {
        const mapping = {
            createRuleNoAuthRequired: 'no-auth-required',
            createRuleOneTimeAuthRequired: 'one-time-client-auth',
            createRuleStandingAuth: 'standing-auth'
        };

        return authorizations.createRuleAllAuth
            ? Object.values(mapping)
            : Object.entries(mapping)
                .filter(([key]) => authorizations[key])
                .map(([, value]) => value);
  }

  createGetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(callCreateCustomRulesGet),
      withLatestFrom(this.store.pipe(select(selectCreateCustomRuleSection))),
      switchMap(([action, customRuleSection]) => {
        return this.customRuleService.callCreateCustomRulesGet().pipe(
          map(apiResponse => callCustomRulesGetSuccessful({ response: apiResponse })),
          catchError(error => of(callCustomRulesGetFailed()))
        );
      })
    )
  );
}






